import { Fragment, useEffect, useRef } from "react";
import PublicHeader from "../../components/layouts/PublicHeader";
import { ABOUT_US_SCHEMA } from "../../utils/schemas/language/pages/aboutusSchema";
import { getLanguageContent } from "../../utils/language/languageSelector";
import { ScrollRestoration } from "react-router-dom";

function AboutUs({ langKey }) {
  const content = getLanguageContent(ABOUT_US_SCHEMA, langKey);

  const containerRef = useRef(null);

  const renderParagraph = (item, index) => {
    if (typeof item === "string") return <p key={index}>{item}</p>;

    if (!item.emphasis || !item.text.includes(item.emphasis)) {
      return <p key={index}>{item.text}</p>;
    }

    const [before, after] = item.text.split(item.emphasis);

    return (
      <p key={index}>
        {before}
        <em>{item.emphasis}</em>
        {after}
      </p>
    );
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const cards = containerRef.current.querySelectorAll(".general-card");
      const containerRect = containerRef.current.getBoundingClientRect();

      // Determine the scroll window based on screen width
      // Mobile cards are taller, so we need more scroll distance (e.g., 600-800)
      const isMobile = window.innerWidth < 768;
      const scrollDistance = isMobile ? 800 : 400;

      const scrollOffset = 150 - containerRect.top;

      cards.forEach((card, index) => {
        const isLastCard = index === cards.length - 1;

        if (isLastCard) {
          card.style.transform = `scale(1)`;
          card.style.visibility = `visible`; // Always visible
          card.style.opacity = `1`;
          return;
        }

        const scrollDistance = window.innerWidth < 768 ? 800 : 400;
        const startPoint = index * scrollDistance;

        const progress = Math.max(
          0,
          Math.min(1, (scrollOffset - startPoint) / scrollDistance),
        );

        if (progress > 0) {
          const scaleValue = 1 - progress * 0.3;
          card.style.transform = `scale(${scaleValue})`;

          // We ADD opacity back here to make sure it actually disappears
          const opacityValue = 1 - progress;
          card.style.opacity = `${opacityValue}`;

          // IMPORTANT: If progress is 1, hide it completely so it doesn't
          // peek out behind the last card
          if (progress >= 1) {
            card.style.visibility = `hidden`;
          } else {
            card.style.visibility = `visible`;
          }
        } else {
          card.style.transform = `scale(1)`;
          card.style.opacity = `1`;
          card.style.visibility = `visible`;
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    // Trigger once on mount to set initial positions
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Fragment>
      <ScrollRestoration />
      <PublicHeader
        title={content.title}
        description={content.sub_title}
        ctas={content.hero_ctas}
      />

      <div id="container">
        <div className="offerings-container">
          <div className="centered-content-container">
            <div className="centered-content">
              <h2>{content.why_exists.heading}</h2>
              <div className="group-content">
                {content.why_exists.content.map(renderParagraph)}
              </div>
            </div>

            {/* Card stack section */}
            <div className="general-container">
              <h2>{content.not_for_everyone.heading}</h2>
              <div className="card-parallax-container" ref={containerRef}>
                {content.not_for_everyone.cards.map((card, idx) => (
                  <div
                    key={idx}
                    className="general-card"
                    /* Pass the index as a CSS variable for the stacking offset */
                    style={{
                      "--index": idx,
                      "--scroll-scale": 1,
                      "--scroll-opacity": 1,
                      "--scroll-blur": "0px",
                    }}
                  >
                    <div className="general-card-left">
                      <img
                        src={process.env.PUBLIC_URL + card.image}
                        alt={card.image_alt}
                      />
                    </div>
                    <div className="general-card-right">
                      <h4>{card.heading}</h4>
                      {card.content.map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="centered-content">
              <h2>{content.closing_sentence.heading}</h2>
              <div className="group-content">
                {content.closing_sentence.content.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
                <p>
                  <strong>
                    <em>{content.closing_sentence.closing}</em>
                  </strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default AboutUs;
